import classNames from 'classnames';
import { ReactElement } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';
import styles from './HeaderRotatingShape.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './HeaderRotatingShape.transitions';
import type { HeaderRotatingShapeProps } from './HeaderRotatingShape.data';
import { findTransitionController, useTransitionController } from 'util/react-transition-component';
import useScrollTrigger from '../../../hooks/useScrollTrigger';
import Heading from '../../atoms/heading/Heading';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useMobileViewport } from '@/hooks/useMobileViewport';
import sharedVariables from '@/data/shared-variable/shared-variables.json';
import BackgroundPinwheel, {
  type BackgroundPinwheelRef,
} from 'components/blocks/header-rotating-shape/BackgroundPinwheel';
import BackgroundLargeCurve, {
  type BackgroundLargeCurveRef,
} from 'components/blocks/header-rotating-shape/BackgroundLargeCurve';
import { useScrollProviderContext } from 'components/unlisted/ScrollProvider';
import { createScrollTrigger } from '@/util/scroll/scrollTrigger.utils';
import BackgroundSmallCurve, {
  type BackgroundSmallCurveRef,
} from 'components/blocks/header-rotating-shape/BackgroundSmallCurve';
import useThemeScrollBlock from '@/hooks/useThemeScrollBlock';
import useClipBlockOverflow from '@/hooks/useClipBlockOverflow';

export default function HeaderRotatingShape({
  blok,
  ref,
  disableScrollTrigger = false,
  ...props
}: HeaderRotatingShapeProps): ReactElement {
  const sectionRef = useRef<HTMLDivElement>(null);
  const localRef = ref || sectionRef;
  const elementRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);
  const titleBottomRightRef = useRef<HTMLHeadingElement>(null);
  const titleTopLeftRef = useRef<HTMLHeadingElement>(null);
  const titleTopLeftMaskRef = useRef<HTMLHeadingElement>(null);
  const titleBottomRightMaskRef = useRef<HTMLHeadingElement>(null);

  const backgroundShapeRef = useRef<
    BackgroundLargeCurveRef | BackgroundPinwheelRef | BackgroundSmallCurveRef
  >(null);
  const scrollContext = useScrollProviderContext();

  const isMobileViewport = useMobileViewport(sharedVariables.deviceState.MIN_768, true);
  const topTitleLines = blok.topTitle.split('\n');
  const bottomTitleLines = blok.bottomTitle.split('\n');
  const isTopTitleMultiline = topTitleLines.length > 1;

  const [isVisible, setIsVisible] = useState(false);
  const [areChildTransitionsReady, setAreChildTransitionsReady] = useState(false);

  const transitionController = useTransitionController(
    () => ({
      refs: {
        titleBottomRightRef,
        titleTopLeftRef,
        titleTopLeftMaskRef,
        titleBottomRightMaskRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [isMobileViewport],
  );

  useThemeScrollBlock(localRef, blok.use_accent_color, {
    foreground: blok.accent_color_foreground?.color || '',
    background: blok.accent_color_background?.color || '',
  });
  useClipBlockOverflow(localRef);

  useChangeTransitionState(transitionController, {
    onMount: true,
    value: isVisible && areChildTransitionsReady,
  });

  useScrollTrigger({
    trigger: localRef,
    vars: {
      start: 'top center',
      end: 'bottom top',
      onEnter() {
        setIsVisible(true);
      },
      onLeaveBack() {
        setIsVisible(false);
      },
      onLeave() {
        setIsVisible(false);
      },
      onEnterBack() {
        setIsVisible(true);
      },
    },
  });

  useLayoutEffect(() => {
    if (disableScrollTrigger) return undefined;

    const triggerElement = localRef.current;
    const revealTimeline = backgroundShapeRef.current?.revealTimelineRef.current;

    if (!triggerElement || !revealTimeline) return undefined;

    const syncRevealTimelineProgress = (progress: number): void => {
      revealTimeline.pause();
      revealTimeline.progress(1 - progress);
    };

    const scrollTrigger = createScrollTrigger(triggerElement, undefined, {
      scroller: scrollContext?.hasSmoothScroll?.current
        ? scrollContext?.scrollWrapperRef?.current || undefined
        : undefined,
      scrub: true,
      start: 'top top',
      end: 'bottom top',
      onUpdate: (self) => {
        syncRevealTimelineProgress(self.progress);
      },
      onRefresh: (self) => {
        syncRevealTimelineProgress(self.progress);
      },
    });

    syncRevealTimelineProgress(scrollTrigger.progress);

    return () => {
      scrollTrigger.kill();
    };
  }, [blok.icon, disableScrollTrigger, localRef, scrollContext, isMobileViewport]);

  useLayoutEffect(() => {
    setAreChildTransitionsReady(false);

    const frameId = window.requestAnimationFrame(() => {
      const refs = [
        titleTopLeftRef,
        titleBottomRightRef,
        titleTopLeftMaskRef,
        titleBottomRightMaskRef,
      ];

      const isReady = refs.every((ref) => ref.current && findTransitionController(ref));
      setAreChildTransitionsReady(isReady);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [isMobileViewport]);

  useLayoutEffect(() => {
    const textContainerElement = textContainerRef.current;
    const titleTopLeftElement = titleTopLeftRef.current;
    const titleBottomRightElement = titleBottomRightRef.current;

    if (!textContainerElement || !titleTopLeftElement || !titleBottomRightElement) return;

    const updateTextContainerMaxWidth = (): void => {
      const titleWidth = Math.max(
        titleTopLeftElement.getBoundingClientRect().width,
        titleBottomRightElement.getBoundingClientRect().width,
      );

      if (!titleWidth) {
        textContainerElement.style.removeProperty('max-width');
        return;
      }

      textContainerElement.style.maxWidth = `${titleWidth * 1.1}px`;
    };

    updateTextContainerMaxWidth();

    const resizeObserver = new ResizeObserver(updateTextContainerMaxWidth);
    resizeObserver.observe(titleTopLeftElement);
    window.addEventListener('resize', updateTextContainerMaxWidth);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateTextContainerMaxWidth);
    };
  }, [isMobileViewport]);

  return (
    <div
      ref={localRef}
      className={classNames(styles.section, props.className, styles[blok.icon])}
      id="headerRotatingShape"
      style={
        {
          '--icon-color': blok?.icon_color?.color,
          ...props.style,
        } as React.CSSProperties
      }
    >
      <div className={styles.container}>
        <div ref={elementRef} className={classNames(styles.headerRotatingShape)}>
          {blok.icon === 'small' && (
            <BackgroundSmallCurve
              ref={backgroundShapeRef as React.Ref<BackgroundSmallCurveRef>}
              iconColor={blok?.icon_color?.color}
              isVisible={isVisible}
              initialRotation={220}
            />
          )}

          {blok.icon === 'large' && (
            <BackgroundLargeCurve
              ref={backgroundShapeRef as React.Ref<BackgroundLargeCurveRef>}
              iconColor={blok?.icon_color?.color}
              isVisible={isVisible}
              initialRotation={-25}
            />
          )}

          {blok.icon === 'pinwheel' && (
            <BackgroundPinwheel
              ref={backgroundShapeRef as React.Ref<BackgroundPinwheelRef>}
              iconColor={blok?.icon_color?.color}
              isVisible={isVisible}
              revealDelay={1.5}
              position={blok.position === 'center' ? { top: '50%', left: '50%' } : undefined}
            />
          )}

          <div ref={textContainerRef} className={styles.textContainer}>
            <span className="text-outline">
              <Heading
                key={`top-left-${isMobileViewport}`}
                ref={titleTopLeftRef}
                as="h2"
                size="heading-03"
                className={classNames(styles.topLeftTitle, {
                  [styles.isMultiline]: isTopTitleMultiline,
                })}
                // transitionInType="revealLinesInOut"
                transitionOutType="splitCharsSlideOutUp"
              >
                {topTitleLines.map((line, index) => (
                  <div key={`${line}-${index}`}>{line}</div>
                ))}
              </Heading>
            </span>

            <span aria-hidden="true">
              <Heading
                key={`top-left-mask-${isMobileViewport}`}
                ref={titleTopLeftMaskRef}
                as="h2"
                size="heading-03"
                className={classNames(styles.topLeftTitle, styles.solid, {
                  [styles.isMultiline]: isTopTitleMultiline,
                })}
                transitionInType="revealLinesInOut"
                transitionOutType="splitCharsSlideOutUp"
              >
                {topTitleLines.map((line, index) => (
                  <div key={`${line}-${index}`}>{line}</div>
                ))}
              </Heading>
            </span>

            <span className="text-outline">
              <Heading
                key={`bottom-right-${isMobileViewport}`}
                ref={titleBottomRightRef}
                as="h2"
                size="heading-03"
                className={classNames(styles.bottomRightTitle)}
                transitionOutType="splitCharsSlideOutUp"
              >
                {bottomTitleLines.map((line, index) => (
                  <div key={`${line}-${index}`}>{line}</div>
                ))}
              </Heading>
            </span>

            <span aria-hidden="true">
              <Heading
                key={`bottom-right-mask-${isMobileViewport}`}
                ref={titleBottomRightMaskRef}
                as="h2"
                size="heading-03"
                className={classNames(styles.bottomRightTitle, styles.solid)}
                transitionInType="revealLinesIn"
                transitionOutType="splitCharsSlideOutUp"
              >
                {bottomTitleLines.map((line, index) => (
                  <div key={`${line}-${index}`}>{line}</div>
                ))}
              </Heading>
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
